import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { AuthProvider } from './admin/contexts/AuthContext';
import Signup from './admin/auth/Signup';
import Login from './admin/auth/Login';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './admin/pages/admin/Dashboard';
import AdminSettings from './admin/pages/admin/Settings';
import CustomerDetailsView from './admin/pages/admin/CustomerDetailsView';
import ContentCreatorDetails from './admin/pages/admin/ContentCreatorDetails';
import ScheduledPosts from './admin/pages/admin/ScheduledPosts';
import AdminContentUpload from './content-creators/ContentUpload';

import AdminQrGenerator from './admin/pages/admin/AdminQrGenerator';
import MultiCustomerAnalytics from './admin/pages/admin/MultiCustomerAnalytics';
import CustomerSocialAccounts from './admin/pages/admin/CustomerSocialAccounts';
import SummaryReport from './admin/pages/admin/SummaryReport';


// Super Admin imports
import SuperAdminLogin from './superadmin/Login';
import SuperAdminDashboard from './superadmin/Dashboard';
import SuperAdminSignup from './superadmin/Signup';
import ViewAssignments from './superadmin/ViewAssignments';
import SuperAdminAnalytics from './superadmin/Analytics';
import AdminManagement from './superadmin/AdminManagement';
import SuperAdminBilling from './superadmin/Billing';

// Customer Integration: Instagram Ads
import InstagramAdsIntegration from './customer/Integration/InstagramAdsIntegration';

// Customer imports
import CustomerDashboard from './customer/Dashboard';
import ContentCalendar from './customer/ContentCalendar';
import CustomerSettings from './customer/Settings';
import CustomerLayout from './customer/Layout';
import CustomerSignup from './customer/auth/CustomerSignup';
import CustomerLogin from './customer/auth/CustomerLogin';
import Subscription from './customer/Subscription';
import ContentReview from './customer/ContentReview';
import MediaLibrary from './customer/MediaLibrary';

import SocialAnalyticsDashboard from './customer/Integration/SocialAnalyticsDashboard';

// Integration imports
import FacebookIntegration from './customer/Integration/FacebookIntegration';
import InstagramIntegration from './customer/Integration/InstagramIntegration';
import YouTubeIntegration from './customer/Integration/YouTubeIntegration';
import LinkedInIntegration from './customer/Integration/LinkedInIntegration';

// Content Creator imports
import ContentCreatorDashboard from './content-creators/Dashboard';
import ContentCreatorSignup from './content-creators/auth/ContentCreatorSignup';
import ContentCreatorLogin from './content-creators/auth/ContentCreatorLogin';
import Assignments from './content-creators/Assignments';
import ContentUpload from './content-creators/ContentUpload';
import Profile from './content-creators/Profile';



import { useAuth } from './admin/contexts/AuthContext';
import TimePeriodChart from './components/TimeperiodChart';
import PostDetailsPage from './components/PostDetailsPage';
import SchedulePostModal from './admin/components/modals/SchedulePostModal';
import ContentDetailView from './admin/components/modals/ContentDetailView';
import CustomerWelcome from './customer/auth/CustomerWelcome';
import Configure from './components/Configure';
import SocialIntegrations from './customer/Integration/SocialIntegrations';
// Docs
import AirSparkDocs from './Airspark_Docs/AirSparkDocs';
import AccountsConfigure from './Airspark_Docs/AccountsConfigure';
import FacebookConnectGuide from './Airspark_Docs/FacebookConnectGuide';

// Import the new component
import CustomerAnalytics from './customer/Analytics';
import ROIDashboard from './customer/ROIDashboard';

// --- ProtectedRoute for all portals ---
function ProtectedRoutePortal({ children, role }) {
  const { currentUser } = useAuth();

  // Not logged in
  if (!currentUser) {
    if (role === 'admin') return <Navigate to="/login" replace />;
    if (role === 'customer') return <Navigate to="/customer/login" replace />;
    if (role === 'content_creator') return <Navigate to="/content-creator/login" replace />;
    if (role === 'superadmin') return <Navigate to="/superadmin/login" replace />;
    return <Navigate to="/login" replace />;
  }

  // Wrong role
  if (role && currentUser.role !== role) {
    if (currentUser.role === 'admin') return <Navigate to="/admin" replace />;
    if (currentUser.role === 'customer') return <Navigate to="/customer" replace />;
    if (currentUser.role === 'content_creator') return <Navigate to="/content-creator" replace />;
    if (currentUser.role === 'superadmin') return <Navigate to="/superadmin" replace />;
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Robust query parser: prefer explicit search params, fall back to hash query.
const parseQuery = () => {
  if (window.location.search && window.location.search.length > 1) {
    try {
      return new URLSearchParams(window.location.search);
    } catch (e) { /* fall through */ }
  }
  const hash = window.location.hash || '';
  const idx = hash.indexOf('?');
  if (idx !== -1) {
    try {
      return new URLSearchParams(hash.substring(idx + 1));
    } catch (e) { /* fall through */ }
  }
  return new URLSearchParams();
};

// NEW: Public wrapper to render SocialIntegrations without auth
function SocialIntegrationsPublic() {
  const [customer, setCustomer] = React.useState(null);
  const [platform, setPlatform] = React.useState('');
  const socialRef = React.useRef(null);

  React.useEffect(() => {
    const params = parseQuery();
    const customerId = params.get('customerId') || params.get('id') || '';
    const customerName = params.get('customerName') || params.get('name') || '';
    const platformParam = params.get('platform') || params.get('p') || '';
    if (customerId) {
      setCustomer({ id: customerId, name: customerName || 'Customer' });
    }
    setPlatform(platformParam);
  }, []);

  if (!customer || !platform) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-white rounded-xl p-6 shadow text-center">
          <h3 className="font-semibold text-slate-900">Missing parameters</h3>
          <p className="text-sm text-slate-600 mt-2">This page expects customerId and platform in the URL query. Example: /#/customer/social-integrations?customerId=xxx&platform=facebook</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-xl p-6 shadow">
        <SocialIntegrations
          ref={socialRef}
          platform={platform}
          customer={customer}
          compact={true}
          onConnectionSuccess={() => {
            // minimal success UX: redirect to home or close
            window.location.href = '/';
          }}
        />
      </div>
    </div>
  );
}

function App() {
  // If the QR was generated with search params but the hash points somewhere else
  // (for example ".../configure?...# /login"), rewrite the hash to load /configure
  // with the original query so Configure.jsx runs under HashRouter.
  useEffect(() => {
    try {
      const search = window.location.search || '';
      if (!search || search.length < 2) return;
      const sp = new URLSearchParams(search);
      const hasCustomer = sp.get('customerId');
      const hasPlatform = sp.get('platform') || sp.get('p');
      if (!hasCustomer || !hasPlatform) return;

      const currentHash = (window.location.hash || '').replace(/^#/, '');
      if (!currentHash.startsWith('/configure')) {
        // preserve the exact search string (including any tokens) when building the new hash
        const qs = sp.toString();
        // set hash to route HashRouter understands: "/configure?{qs}"
        window.location.hash = `/configure?${qs}`;
      }
    } catch (e) {
      // ignore errors silently
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
          <div className="min-h-screen bg-gray-50">
            <Routes>
              {/* Public Auth Routes */}
              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword userType="admin" />} />
              <Route path="/customer/signup" element={<CustomerSignup />} />
              <Route path="/customer/login" element={<CustomerLogin />} />
              <Route path="/customer/forgot-password" element={<ForgotPassword userType="customer" />} />
              <Route path="/content-creator/signup" element={<ContentCreatorSignup />} />
              <Route path="/content-creator/login" element={<ContentCreatorLogin />} />
              <Route path="/content-creator/forgot-password" element={<ForgotPassword userType="content_creator" />} />
              
              {/* QR Code Configure Route - Public (no authentication required) */}
              <Route path="/configure" element={<Configure />} />

              {/* NEW: Public direct access to SocialIntegrations (no login required) */}
              <Route path="/customer/social-integrations" element={<SocialIntegrationsPublic />} />
              <Route path="/social-integrations" element={<SocialIntegrationsPublic />} />
              
              {/* Customer Integration Routes - Public (for QR code access) */}
              <Route path="/customer/integration/facebook" element={<FacebookIntegration />} />
              <Route path="/customer/integration/instagram" element={<InstagramIntegration />} />
              <Route path="/customer/integration/youtube" element={<YouTubeIntegration />} />
              <Route path="/customer/integration/linkedin" element={<LinkedInIntegration />} />
              {/* Public route for Instagram Ads Integration */}
              <Route path="/customer/integration/instagram-ads" element={<InstagramAdsIntegration />} />
              
              {/* Super Admin Auth Routes */}
              <Route path="/superadmin/signup" element={<SuperAdminSignup />} />
              <Route path="/superadmin/login" element={<SuperAdminLogin />} />

              {/* Customer Media Library & Analytics */}
              <Route path="/customer/media-library" element={<MediaLibrary />} />
              
              {/* Super Admin Portal (protected) */}
              <Route path="/superadmin" element={
                <ProtectedRoutePortal role="superadmin">
                  <SuperAdminDashboard />
                </ProtectedRoutePortal>
              } />
              <Route path="/superadmin/dashboard" element={
                <ProtectedRoutePortal role="superadmin">
                  <SuperAdminDashboard />
                </ProtectedRoutePortal>
              } />
              <Route path="/superadmin/view-assignments" element={
                <ProtectedRoutePortal role="superadmin">
                  <ViewAssignments />
                </ProtectedRoutePortal>
              } />
              <Route path="/superadmin/analytics" element={
                <ProtectedRoutePortal role="superadmin">
                  <SuperAdminAnalytics />
                </ProtectedRoutePortal>
              } />
              <Route path="/superadmin/admin-management" element={
                <ProtectedRoutePortal role="superadmin">
                  <AdminManagement />
                </ProtectedRoutePortal>
              } />
              <Route path="/superadmin/billing" element={
                <ProtectedRoutePortal role="superadmin">
                  <SuperAdminBilling />
                </ProtectedRoutePortal>
              } />
              <Route path="/superadmin/qr-generator" element={
                <ProtectedRoutePortal role="superadmin">
                  <AdminQrGenerator />
                </ProtectedRoutePortal>
              } />

              {/* Admin Portal (protected) */}
              <Route path="/admin" element={
                <ProtectedRoutePortal role="admin">
                  <Dashboard />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoutePortal role="admin">
                  <Dashboard />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/customer-details/:id" element={
                <ProtectedRoutePortal role="admin">
                  <CustomerDetailsView />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/content-creator-details/:id" element={
                <ProtectedRoutePortal role="admin">
                  <ContentCreatorDetails />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/scheduled-posts" element={
                <ProtectedRoutePortal role="admin">
                  <ScheduledPosts />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/content-upload/:calendarId/:itemIndex" element={
                <ProtectedRoutePortal role="admin">
                  <AdminContentUpload />
                </ProtectedRoutePortal>
              } />
              {/* Admin Customer Social Accounts Overview */}
              <Route path="/admin/social-accounts" element={
                <ProtectedRoutePortal role="admin">
                  <CustomerSocialAccounts />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/summary-report" element={
                <ProtectedRoutePortal role="admin">
                  <SummaryReport />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/social-analytics-dashboard" element={
                <ProtectedRoutePortal role="admin">
                  <SocialAnalyticsDashboard />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/timeperiod-chart" element={
                <ProtectedRoutePortal role="admin">
                  <TimePeriodChart />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/post-details" element={
                <ProtectedRoutePortal role="admin">
                  <PostDetailsPage />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/schedule-post-modal" element={
                <ProtectedRoutePortal role="admin">
                  <SchedulePostModal />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/content-detail-view" element={
                <ProtectedRoutePortal role="admin">
                  <ContentDetailView />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/qr-generator" element={
                <ProtectedRoutePortal role="admin">
                  <AdminQrGenerator />
                </ProtectedRoutePortal>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoutePortal role="admin">
                  <AdminSettings />
                </ProtectedRoutePortal>
              } />
              {/* Admin Multi-Customer Analytics Dashboard */}
              <Route path="/admin/customer-analytics" element={
                <ProtectedRoutePortal role="admin">
                  <MultiCustomerAnalytics />
                </ProtectedRoutePortal>
              } />

              {/* Remove routes for Customer Portfolio Overview since module is missing */}

              {/* Customer Portal (protected) */}
              <Route path="/customer/welcome" element={
                <CustomerWelcome />
              } />

              <Route path="/customer" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <CustomerDashboard />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/calendar" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <ContentCalendar />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/settings" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <CustomerSettings />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/subscription" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <Subscription />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/content-review" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <ContentReview />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/upload" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <ContentUpload />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              {/* Add route for Customer Analytics */}
              <Route path="/customer/analytics" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <CustomerAnalytics />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              {/* Add route for ROI Dashboard */}
              <Route path="/customer/roi-dashboard" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <ROIDashboard />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/social-analytics-dashboard" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <SocialAnalyticsDashboard />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/timeperiod-chart" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <TimePeriodChart />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              <Route path="/customer/post-details" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <PostDetailsPage />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />
              {/* Protected route for Instagram Ads Integration (Customer) */}
              <Route path="/customer/instagram-ads-integration" element={
                <ProtectedRoutePortal role="customer">
                  <CustomerLayout>
                    <InstagramAdsIntegration />
                  </CustomerLayout>
                </ProtectedRoutePortal>
              } />

              {/* Content Creator Portal (protected) */}
              <Route path="/content-creator" element={
                <ProtectedRoutePortal role="content_creator">
                  <ContentCreatorDashboard />
                </ProtectedRoutePortal>
              } />
              <Route path="/content-creator/assignments" element={
                <ProtectedRoutePortal role="content_creator">
                  <Assignments />
                </ProtectedRoutePortal>
              } />
              <Route path="/content-creator/content-upload/:calendarId/:itemIndex" element={
                <ProtectedRoutePortal role="content_creator">
                  <ContentUpload />
                </ProtectedRoutePortal>
              } />
              <Route path="/content-creator/upload/:calendarId/:itemIndex" element={
                <ProtectedRoutePortal role="content_creator">
                  <ContentUpload />
                </ProtectedRoutePortal>
              } />
              <Route path="/content-creator/upload/:assignmentId" element={
                <ProtectedRoutePortal role="content_creator">
                  <ContentUpload />
                </ProtectedRoutePortal>
              } />
              <Route path="/content-creator/upload" element={
                <ProtectedRoutePortal role="content_creator">
                  <ContentUpload />
                </ProtectedRoutePortal>
              } />
              <Route path="/content-creator/profile" element={
                <ProtectedRoutePortal role="content_creator">
                  <Profile />
                </ProtectedRoutePortal>
              } />
              
              <Route path="/docs" element={<AirSparkDocs />} />
              <Route path="/docs/accounts-configure" element={<AccountsConfigure />} />
              <Route path="/docs/facebook-connect" element={<FacebookConnectGuide />} />
             
              {/* Default route */}
              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
